/**
 * IConnectAdapter implementation backed by the real Amazon Connect Streams SDK.
 *
 * Currently delegates to the existing AmazonConnectService singleton.
 * In Phase 4 the service logic moves directly into this class.
 */

import { amazonConnectService as _service } from '@/services/amazonConnect';
import type {
  ConnectContact,
  ConnectCallback,
  ConnectAgent,
  ConnectAgentStateDefinition,
  ConnectChatController,
  ConnectQueueEndpoint,
} from '@/types';
import type {
  IConnectAdapter,
  AgentStateTarget,
  Unsubscribe,
  InitOptions,
  OutboundOptions,
  CcpEnums,
} from './types';

// ---------------------------------------------------------------------------
// CCP enum reader — replaces ad-hoc `window.connect as unknown as {…}` casts
// across contactUtils.ts and other files.  All access goes through this one
// function so test fakes can provide their own enum values.
// ---------------------------------------------------------------------------

function readCcpEnums(): CcpEnums {
  const c = (window as unknown as { connect?: Record<string, unknown> })
    .connect;
  // Provide typed defaults so callers never get undefined enum lookups.
  return {
    ContactStateType: (c?.ContactStateType ?? {}) as Record<string, unknown>,
    ContactType: (c?.ContactType ?? {}) as Record<string, unknown>,
    AgentStateType: (c?.AgentStateType ?? {}) as Record<string, unknown>,
    ConnectionType: (c?.ConnectionType ?? {}) as Record<string, unknown>,
    byPhoneNumber: (phone: string) => {
      const Endpoint = c?.Endpoint as
        | { byPhoneNumber?: (p: string) => unknown }
        | undefined;
      if (!Endpoint?.byPhoneNumber) {
        throw new Error('Amazon Connect Streams library not available');
      }
      return Endpoint.byPhoneNumber(phone);
    },
    createAgent: () => {
      const Agent = c?.Agent as (new () => unknown) | undefined;
      if (!Agent)
        throw new Error('Amazon Connect Streams library not available');
      return new Agent() as ConnectAgent;
    },
  };
}

// ---------------------------------------------------------------------------
// Adapter — delegates to the existing singleton, adds getCcpEnums()
// ---------------------------------------------------------------------------

export class StreamsConnectAdapter implements IConnectAdapter {
  // -- lifecycle --
  getUserIdentity(): Promise<{ email: string; name: string } | null> {
    return _service.getUserIdentity();
  }
  async initCCP(container: HTMLElement, options?: InitOptions): Promise<void> {
    return _service.initializeCCP(container, options);
  }
  resetSession(): void {
    _service.resetCCPSession();
  }

  // -- CCP events --
  onReady(cb: () => void): Unsubscribe {
    return _service.onInitialized(cb);
  }
  onAuthFail(cb: () => void): Unsubscribe {
    return _service.onAuthFail(cb);
  }
  onAuthRetriesExhausted(cb: () => void): Unsubscribe {
    return _service.onAuthorizeRetriesExhausted(cb);
  }
  onAgent(cb: (agent: ConnectAgent) => void): Unsubscribe {
    return _service.onAgent(cb as (agent: unknown) => void);
  }
  onContact(cb: (contact: ConnectContact) => void): Unsubscribe {
    return _service.onContact(cb as (contact: unknown) => void);
  }

  // -- agent ops --
  setAgentState(target: AgentStateTarget, cb?: ConnectCallback): void {
    _service.setAgentState(target, cb);
  }
  setAgentStateByDefinition(
    def: ConnectAgentStateDefinition,
    cb?: ConnectCallback
  ): void {
    _service.setAgentStateByDefinition(def, cb);
  }
  setAgentStateByName(name: string, cb?: ConnectCallback): void {
    _service.setAgentStateByName(name, cb);
  }
  async setAgentOffline(): Promise<void> {
    return _service.setAgentOffline();
  }

  // -- contact actions --
  acceptContact(contact: ConnectContact, cb?: ConnectCallback): void {
    _service.acceptContact(contact, cb);
  }
  rejectContact(contact: ConnectContact, cb?: ConnectCallback): void {
    _service.rejectContact(contact, cb);
  }
  getCallScopeContactId(contact: ConnectContact): string {
    return _service.getCallScopeContactId(contact);
  }

  // -- outbound dial --
  connectOutbound(
    phone: string,
    cb?: ConnectCallback,
    opts?: OutboundOptions
  ): void {
    _service.connectOutbound(phone, cb ?? {}, opts);
  }
  markDialStarted(): void {
    _service.markAgentConnectOutboundDialStarted();
  }
  clearDialContact(id: string): void {
    _service.clearAgentConnectOutboundContact(id);
  }
  isDialContact(contact: ConnectContact): boolean {
    return _service.isAgentConnectOutboundContact(contact);
  }

  // -- monitor sessions --
  setExpectedMonitor(sessionId: string, targetId: string): void {
    _service.setExpectedMonitorSession(sessionId, targetId);
  }
  clearExpectedMonitor(): void {
    _service.clearExpectedMonitorSession();
  }
  isMonitorContact(contact: ConnectContact): boolean {
    return _service.isExpectedMonitorContact(contact);
  }

  // -- transfers --
  transferToPhone(
    contact: ConnectContact,
    phone: string,
    cb?: ConnectCallback
  ): void {
    _service.transferToPhone(contact, phone, cb ?? {});
  }
  transferToQueue(
    contact: ConnectContact,
    endpoint: ConnectQueueEndpoint,
    cb?: ConnectCallback
  ): void {
    _service.transferToQueue(contact, endpoint, cb ?? {});
  }
  addConnection(
    contact: ConnectContact,
    endpoint: unknown,
    cb?: ConnectCallback
  ): void {
    _service.addWarmTransferConsult(
      contact,
      endpoint as ConnectQueueEndpoint,
      cb ?? {}
    );
  }
  holdConnection(contact: ConnectContact, cb?: ConnectCallback): void {
    _service.startWarmTransferHold(contact, cb ?? {});
  }
  resumeConnection(contact: ConnectContact, cb?: ConnectCallback): void {
    _service.conferenceWarmTransfer(contact, cb ?? {});
  }
  destroyAgentConnection(contact: ConnectContact, cb?: ConnectCallback): void {
    _service.completeWarmTransfer(contact, cb ?? {});
  }
  destroyConsultConnection(
    contact: ConnectContact,
    connectionId: string | null,
    cb?: ConnectCallback
  ): void {
    _service.cancelWarmTransferConsult(contact, connectionId ?? '', cb ?? {});
  }
  getQueueEndpoints(
    agent: ConnectAgent,
    queueARNs: string[],
    cb: {
      success: (data: { endpoints: ConnectQueueEndpoint[] }) => void;
      failure?: (err?: Error) => void;
    }
  ): void {
    _service.getQueueEndpoints(agent, queueARNs, cb);
  }

  // -- chat --
  isChatContact(contact: ConnectContact): boolean {
    return _service.isChatContact(contact);
  }
  async getChatMediaController(
    contact: ConnectContact
  ): Promise<ConnectChatController> {
    return _service.getChatMediaController(contact);
  }
  async handleChatAccepted(contact: ConnectContact): Promise<void> {
    return _service.handleAgentChatAccepted(contact);
  }
  sendChatMessage(
    contact: ConnectContact,
    content: string,
    cb?: ConnectCallback
  ): void {
    _service.sendChatMessage(contact, content, cb);
  }

  // -- media --
  async getFrameMediaDevices(timeoutMs: number): Promise<MediaDeviceInfo[]> {
    return _service.getFrameMediaDevices(timeoutMs);
  }

  // -- CCP enum accessors --
  getCcpEnums(): CcpEnums {
    return readCcpEnums();
  }
}

/** Singleton adapter instance. */
export const connectAdapter: IConnectAdapter = new StreamsConnectAdapter();
